import { useRef, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Flex,
  HStack,
  Table,
  Text,
  VStack,
  Spinner,
  Separator,
  NativeSelect,
  IconButton,
  Field,
} from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DialogComponent,
  StatusBadge,
  InputField,
} from '@themes/aegix';
import { HiArrowUpTray, HiArrowDownTray, HiXMark, HiTrash, HiPlus } from 'react-icons/hi2';
import { useUploadBulkInvite, useSubmitBulkInvite, useGroups, useSites } from '../../hooks/queries';
import { toaster } from '../ui/toaster';
import type { BulkInviteUploadResponse, BulkInviteRowPayload, Group, Site } from '../../services';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

interface BulkInviteDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

// Client-side whitelist matching standard user types expected by the backend
// (normalizes to a user_type via _ROLE_BY_KEY/_ROLE_BY_LABEL in server/apps/invite/bulk.py).
// If a new backend role/user_type is added to USER_TYPE_CHOICES, update this set to match.
const VALID_ROLES = new Set([
  'district admin',
  'district_admin',
  'school admin',
  'school_admin',
  'site admin',
  'site_admin',
  'teacher',
  'staff',
  'responder',
  'base user',
  'base_user',
  'read only',
  'read_only',
]);

const bulkInviteRowSchema = z.object({
  email: z.string().email('Email is invalid').min(1, 'Email is required'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  role: z.string().min(1, 'Role is required'),
  site: z.string().min(1, 'Site is required'),
});

const bulkInviteFormSchema = z.object({
  rows: z.array(bulkInviteRowSchema),
});

type BulkInviteFormValues = z.infer<typeof bulkInviteFormSchema>;

export function BulkInviteDialog({ isOpen, onClose }: BulkInviteDialogProps) {
  const { t } = useTranslation();
  // Tab control
  const [activeTab, setActiveTab] = useState<'file' | 'several'>('file');

  // File Upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileResponse, setFileResponse] = useState<BulkInviteUploadResponse | null>(null);
  const [topLevelError, setTopLevelError] = useState<string | null>(null);

  const uploadMutation = useUploadBulkInvite();
  const submitMutation = useSubmitBulkInvite();

  // Roles & Sites lookup queries
  const { data: groupsData } = useGroups({ limit: 1000 });
  const { data: sitesData } = useSites({ limit: 1000 });

  // Add Several form state
  const methods = useForm<BulkInviteFormValues>({
    resolver: zodResolver(bulkInviteFormSchema),
    defaultValues: {
      rows: [{ email: '', first_name: '', last_name: '', role: '', site: '' }],
    },
    mode: 'onChange',
  });

  const { register, control, formState: { errors }, reset, getValues } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rows',
  });

  const [rowStatuses, setRowStatuses] = useState<
    Record<number, { status: 'created' | 'skipped' | 'error'; reason?: string }>
  >({});

  const handleClose = () => {
    setFile(null);
    setFileResponse(null);
    setTopLevelError(null);
    setRowStatuses({});
    reset({
      rows: [{ email: '', first_name: '', last_name: '', role: '', site: '' }],
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setActiveTab('file');
    onClose();
  };

  const handleDownloadTemplate = () => {
    const headers = ['email', 'first_name', 'last_name', 'role', 'site'];
    const csvContent = 'data:text/csv;charset=utf-8,' + headers.join(',') + '\n';
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'user_bulk_invite_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadFailedRows = () => {
    if (!fileResponse) return;
    const failedRows = fileResponse.results.filter((r) => r.status === 'error');
    if (failedRows.length === 0) return;

    const headers = ['Row Number', 'Email', 'Error Reason'];
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(',')]
        .concat(
          failedRows.map(
            (r) =>
              `${r.index},"${r.email}","${(r.reason || '').replace(/"/g, '""')}"`
          )
        )
        .join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'failed_invites_report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const name = selectedFile.name.toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.xlsx')) {
      setTopLevelError('Invalid file type. Please upload a .csv or .xlsx file.');
      setFile(null);
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (selectedFile.size > MAX_FILE_SIZE) {
      setTopLevelError('File is too large. Maximum file size is 5MB.');
      setFile(null);
      return;
    }

    setFile(selectedFile);
    setTopLevelError(null);
    setFileResponse(null);
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setTopLevelError(null);
      const res = await uploadMutation.mutateAsync(file);
      setFileResponse(res);
      if (res.summary.error > 0) {
        toaster.create({
          title: 'Upload completed with some errors',
          description: `Successfully processed ${res.summary.created} user(s), skipped ${res.summary.skipped}, failed ${res.summary.error}.`,
          type: 'warning',
        });
      } else {
        toaster.create({
          title: 'Bulk invites processed successfully',
          description: `Successfully invited ${res.summary.created} user(s).`,
          type: 'success',
        });
      }
    } catch (err) {
      setTopLevelError((err as Error).message || t('users.failedToProcessBulkInvite', 'Failed to process bulk invite.'));
      toaster.create({
        title: t('users.bulkInviteUploadFailed', 'Bulk invite upload failed'),
        description: (err as Error).message,
        type: 'error',
      });
    }
  };

  const handleFileReset = () => {
    setFile(null);
    setFileResponse(null);
    setTopLevelError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFieldChange = (index: number) => {
    if (rowStatuses[index]) {
      setRowStatuses((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const onSubmitSeveral = async (values: BulkInviteFormValues) => {
    const submittedOriginalIndices: number[] = [];
    const rowsToSubmit: BulkInviteRowPayload[] = values.rows
      .map((row, idx) => {
        const status = rowStatuses[idx]?.status;
        if (status === 'created' || status === 'skipped') return null;
        submittedOriginalIndices.push(idx);
        return row;
      })
      .filter((row): row is BulkInviteFormValues['rows'][number] => row !== null);

    if (rowsToSubmit.length === 0) {
      toaster.create({
        title: t('users.noRowsToSubmit', 'No rows to submit'),
        description: t('users.allRowsProcessed', 'All rows have already been successfully processed.'),
        type: 'info',
      });
      return;
    }

    try {
      // Optimistically clear errors for submitting rows
      setRowStatuses((prev) => {
        const next = { ...prev };
        submittedOriginalIndices.forEach((idx) => {
          delete next[idx];
        });
        return next;
      });

      const res = await submitMutation.mutateAsync(rowsToSubmit);

      // Map outcomes back to original indices
      setRowStatuses((prev) => {
        const next = { ...prev };
        res.results.forEach((rowResult) => {
          const origIdx = submittedOriginalIndices[rowResult.index - 1];
          if (origIdx !== undefined) {
            next[origIdx] = {
              status: rowResult.status,
              reason: rowResult.reason,
            };
          }
        });
        return next;
      });

      if (res.summary.error > 0) {
        toaster.create({
          title: t('users.bulkInvitesCompletedWithErrors', 'Bulk invites completed with some errors'),
          description: t('users.bulkInvitesSummaryErrors', 'Successfully created {{created}} user(s), skipped {{skipped}}, failed {{error}}.', {
            created: res.summary.created,
            skipped: res.summary.skipped,
            error: res.summary.error,
          }),
          type: 'warning',
        });
      } else {
        toaster.create({
          title: t('users.bulkInvitesSuccessTitle', 'Bulk invites processed successfully'),
          description: t('users.bulkInvitesSuccessDesc', 'Successfully invited {{created}} user(s).', {
            created: res.summary.created,
          }),
          type: 'success',
        });
        handleClose();
      }
    } catch (err) {
      toaster.create({
        title: t('users.bulkInviteSubmissionFailed', 'Bulk invite submission failed'),
        description: (err as Error).message,
        type: 'error',
      });
    }
  };

  const isPending = uploadMutation.isPending || submitMutation.isPending;

  return (
    <DialogComponent
      e2eTitle="bulk-invite-dialog"
      isOpen={isOpen}
      onClose={handleClose}
      headingText={t('users.bulkInviteUsers', 'Bulk Invite Users')}
      width={activeTab === 'several' ? '5xl' : '3xl'}
      isShowClose={!isPending}
    >
      <VStack align="stretch" gap={5} pt={4}>
        {/* Tab switchers */}
        {!isPending && !fileResponse && (
          <Flex borderBottomWidth="1px" borderColor="border.secondary" mb={2} gap={6}>
            <Box
              pb={2}
              cursor="pointer"
              fontWeight="semibold"
              fontSize="sm"
              color={activeTab === 'file' ? 'button.primary' : 'text.secondary'}
              borderBottomWidth={activeTab === 'file' ? '2px' : '0px'}
              borderColor="button.primary"
              onClick={() => setActiveTab('file')}
              data-e2e="bulk-invite-tab-upload"
            >
              {t('users.uploadFileTab', 'Upload File')}
            </Box>
            <Box
              pb={2}
              cursor="pointer"
              fontWeight="semibold"
              fontSize="sm"
              color={activeTab === 'several' ? 'button.primary' : 'text.secondary'}
              borderBottomWidth={activeTab === 'several' ? '2px' : '0px'}
              borderColor="button.primary"
              onClick={() => setActiveTab('several')}
              data-e2e="bulk-invite-tab-several"
            >
              {t('users.addSeveralTab', 'Add Several Users')}
            </Box>
          </Flex>
        )}

        {/* Tab content 1: File Upload */}
        {activeTab === 'file' && (
          <>
            {!fileResponse && !isPending && (
              <>
                <Text fontSize="sm" color="text.secondary">
                  {t(
                    'users.fileUploadNotice',
                    'Invite multiple users at once by uploading a CSV or Excel (.xlsx) file. Please download the template to ensure your headers and roles are formatted correctly.',
                  )}
                </Text>

                <Box
                  borderWidth="2px"
                  borderStyle="dashed"
                  borderColor={file ? 'border.action' : 'border.secondary'}
                  borderRadius="lg"
                  p={8}
                  textAlign="center"
                  cursor="pointer"
                  _hover={{ bg: 'surface.secondary' }}
                  onClick={() => fileInputRef.current?.click()}
                  data-e2e="bulk-invite-dropzone"
                >
                  <VStack gap={2}>
                    <HiArrowUpTray size={32} color="var(--chakra-colors-text-secondary)" />
                    {file ? (
                      <Text fontSize="md" fontWeight="semibold" color="text.primary">
                        {file.name}
                      </Text>
                    ) : (
                      <Text fontSize="md" fontWeight="semibold" color="text.primary">
                        {t('users.clickToSelectFile', 'Click to select CSV or XLSX file')}
                      </Text>
                    )}
                    <Text fontSize="xs" color="text.secondary">
                      {file
                        ? t('users.fileSizeKb', 'Size: {{size}} KB', {
                            size: (file.size / 1024).toFixed(1),
                          })
                        : t(
                            'users.maxFileSizeNotice',
                            'Max file size 5MB. Supports CSV and XLSX.',
                          )}
                    </Text>
                  </VStack>
                </Box>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  aria-label="Invite CSV or XLSX file"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  data-e2e="bulk-invite-file-input"
                  data-testid="bulk-invite-file-input"
                />

                <HStack justify="space-between" align="center">
                  <ButtonComponent
                    type="button"
                    visual="secondary"
                    icon={<HiArrowDownTray />}
                    onClick={handleDownloadTemplate}
                    data-e2e="bulk-invite-download-template"
                  >
                    {t('users.downloadTemplate', 'Download Template')}
                  </ButtonComponent>
                  {file && (
                    <ButtonComponent
                      type="button"
                      visual="secondary"
                      icon={<HiXMark />}
                      onClick={handleFileReset}
                      data-e2e="bulk-invite-clear-file"
                    >
                      {t('users.clearSelection', 'Clear Selection')}
                    </ButtonComponent>
                  )}
                </HStack>
              </>
            )}

            {isPending && (
              <Flex direction="column" align="center" justify="center" py={12} gap={4}>
                <Spinner size="xl" color="button.primary" />
                <Text fontSize="md" fontWeight="medium" color="text.primary">
                  {t('users.processingInvites', 'Processing and sending invites...')}
                </Text>
                <Text fontSize="sm" color="text.secondary">
                  {t(
                    'users.doNotCloseModalNotice',
                    'Please do not close this modal or refresh the page.',
                  )}
                </Text>
              </Flex>
            )}

            {topLevelError && (
              <Alert.Root status="error" data-e2e="bulk-invite-error-alert">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>{t('users.processingError', 'Processing Error')}</Alert.Title>
                  <Alert.Description>{topLevelError}</Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            {fileResponse && !isPending && (
              <VStack align="stretch" gap={4}>
                <Text fontSize="md" fontWeight="semibold" color="text.primary">
                  {t('users.processingResults', 'Processing Results')}
                </Text>

                <Separator />

                {/* Summary Statistics Cards */}
                <Flex gap={4} width="100%">
                  <Box
                    flex={1}
                    p={4}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="border.secondary"
                    bg="surface.secondary"
                    data-e2e="summary-card-total"
                  >
                    <Text fontSize="xs" fontWeight="semibold" color="text.secondary" textTransform="uppercase">
                      {t('users.totalRows', 'Total Rows')}
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="text.primary">
                      {fileResponse.summary.total}
                    </Text>
                  </Box>

                  <Box
                    flex={1}
                    p={4}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="green.200"
                    bg="green.50/20"
                    data-e2e="summary-card-created"
                  >
                    <Text fontSize="xs" fontWeight="semibold" color="green.700" textTransform="uppercase">
                      {t('users.createdStatus', 'Created')}
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {fileResponse.summary.created}
                    </Text>
                  </Box>

                  <Box
                    flex={1}
                    p={4}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.200"
                    bg="gray.50/20"
                    data-e2e="summary-card-skipped"
                  >
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase">
                      {t('users.skippedStatus', 'Skipped')}
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="gray.500">
                      {fileResponse.summary.skipped}
                    </Text>
                  </Box>

                  <Box
                    flex={1}
                    p={4}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="red.200"
                    bg="red.50/20"
                    data-e2e="summary-card-error"
                  >
                    <Text fontSize="xs" fontWeight="semibold" color="red.700" textTransform="uppercase">
                      {t('users.errorsStatus', 'Errors')}
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" color="red.600">
                      {fileResponse.summary.error}
                    </Text>
                  </Box>
                </Flex>

                {/* Results Table */}
                <Box
                  borderWidth="1px"
                  borderColor="border.secondary"
                  borderRadius="md"
                  overflow="hidden"
                  maxHeight="250px"
                  overflowY="auto"
                  data-e2e="bulk-invite-results-table-container"
                >
                  <Table.Root size="sm" interactive>
                    <Table.Header bg="surface.secondary" position="sticky" top="0" zIndex={1}>
                      <Table.Row>
                        <Table.ColumnHeader width="80px">{t('users.colRow', 'Row')}</Table.ColumnHeader>
                        <Table.ColumnHeader width="220px">{t('common.email', 'Email')}</Table.ColumnHeader>
                        <Table.ColumnHeader width="120px">{t('common.status', 'Status')}</Table.ColumnHeader>
                        <Table.ColumnHeader>{t('users.colDetailsReason', 'Details / Reason')}</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {fileResponse.results.map((row) => (
                        <Table.Row key={row.index} data-e2e={`result-row-${row.index}`}>
                          <Table.Cell>{row.index}</Table.Cell>
                          <Table.Cell fontWeight="medium">{row.email || '—'}</Table.Cell>
                          <Table.Cell>
                            <StatusBadge
                              status={
                                row.status === 'created'
                                  ? 'success'
                                  : row.status === 'skipped'
                                  ? 'default'
                                  : 'error'
                              }
                              label={
                                row.status === 'created'
                                  ? t('users.createdStatus', 'Created')
                                  : row.status === 'skipped'
                                  ? t('users.skippedStatus', 'Skipped')
                                  : t('users.errorStatus', 'Error')
                              }
                            />
                          </Table.Cell>
                          <Table.Cell
                            color={row.status === 'error' ? 'text.danger' : 'text.secondary'}
                            fontSize="xs"
                          >
                            {row.reason || '—'}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Box>

                {fileResponse.summary.error > 0 && (
                  <HStack justify="flex-end">
                    <ButtonComponent
                      type="button"
                      visual="secondary"
                      icon={<HiArrowDownTray />}
                      onClick={handleDownloadFailedRows}
                      data-e2e="bulk-invite-download-errors"
                    >
                      {t('users.downloadErrorReport', 'Download Error Report')}
                    </ButtonComponent>
                  </HStack>
                )}
              </VStack>
            )}

            <HStack justify="flex-end" gap={3} borderTopWidth="1px" borderColor="border.secondary" pt={4}>
              {!fileResponse && !isPending && (
                <>
                  <ButtonComponent
                    type="button"
                    visual="secondary"
                    onClick={handleClose}
                    data-e2e="bulk-invite-cancel"
                  >
                    {t('common.cancel', 'Cancel')}
                  </ButtonComponent>
                  <ButtonComponent
                    type="button"
                    visual="primary"
                    onClick={handleUpload}
                    disabled={!file}
                    data-e2e="bulk-invite-submit"
                  >
                    {t('users.uploadFileTab', 'Upload File')}
                  </ButtonComponent>
                </>
              )}

              {fileResponse && !isPending && (
                <>
                  {fileResponse.summary.error > 0 && (
                    <ButtonComponent
                      type="button"
                      visual="secondary"
                      onClick={handleFileReset}
                      data-e2e="bulk-invite-retry-btn"
                    >
                      {t('users.uploadCorrectedFile', 'Upload corrected file')}
                    </ButtonComponent>
                  )}
                  <ButtonComponent
                    type="button"
                    visual="primary"
                    onClick={handleClose}
                    data-e2e="bulk-invite-close"
                  >
                    {t('common.close', 'Close')}
                  </ButtonComponent>
                </>
              )}
            </HStack>
          </>
        )}

        {/* Tab content 2: Add Several Users */}
        {activeTab === 'several' && (
          <FormProvider {...methods}>
            <VStack align="stretch" gap={4}>
              <Text fontSize="sm" color="text.secondary">
                {t(
                  'users.addSeveralNotice',
                  'Add several users inline. Specify their email, names, role, and associated school site. Rows that fail to save will remain editable with details.',
                )}
              </Text>

              {isPending && (
                <Flex direction="column" align="center" justify="center" py={12} gap={4}>
                  <Spinner size="xl" color="button.primary" />
                  <Text fontSize="md" fontWeight="medium" color="text.primary">
                    {t('users.savingUsersAndInvites', 'Saving users and sending invites...')}
                  </Text>
                </Flex>
              )}

              {!isPending && (
                <>
                  <Box
                    borderWidth="1px"
                    borderColor="border.secondary"
                    borderRadius="md"
                    overflow="hidden"
                    maxHeight="350px"
                    overflowY="auto"
                  >
                    <Table.Root size="sm" interactive>
                      <Table.Header bg="surface.secondary" position="sticky" top="0" zIndex={1}>
                        <Table.Row>
                          <Table.ColumnHeader width="220px">{t('common.email', 'Email')}</Table.ColumnHeader>
                          <Table.ColumnHeader width="150px">{t('users.colFirstName', 'First Name')}</Table.ColumnHeader>
                          <Table.ColumnHeader width="150px">{t('users.colLastName', 'Last Name')}</Table.ColumnHeader>
                          <Table.ColumnHeader width="180px">{t('users.colRole', 'Role')}</Table.ColumnHeader>
                          <Table.ColumnHeader width="180px">{t('users.colSite', 'Site')}</Table.ColumnHeader>
                          <Table.ColumnHeader width="60px"></Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {fields.map((item, index) => {
                          const outcome = rowStatuses[index];
                          const isSuccess = outcome?.status === 'created' || outcome?.status === 'skipped';
                          const hasError = outcome?.status === 'error';

                          return (
                            <Fragment key={item.id}>
                              <Table.Row
                                data-e2e={`bulk-invite-row-${index}`}
                                bg={hasError ? 'red.50/5' : undefined}
                              >
                                <Table.Cell>
                                  <Field.Root invalid={!!errors.rows?.[index]?.email}>
                                    <InputField
                                      size="sm"
                                      aria-label={`Email — row ${index + 1}`}
                                      placeholder="email@example.com"
                                      disabled={isSuccess}
                                      {...register(`rows.${index}.email` as const, {
                                        onChange: () => handleFieldChange(index),
                                      })}
                                      data-e2e={`bulk-invite-email-${index}`}
                                      data-testid={`bulk-invite-email-${index}`}
                                    />
                                    {errors.rows?.[index]?.email && (
                                      <Text fontSize="2xs" color="text.danger" mt={0.5}>
                                        {errors.rows[index]?.email?.message}
                                      </Text>
                                    )}
                                  </Field.Root>
                                </Table.Cell>

                                <Table.Cell>
                                  <Field.Root invalid={!!errors.rows?.[index]?.first_name}>
                                    <InputField
                                      size="sm"
                                      aria-label={`First name — row ${index + 1}`}
                                      placeholder="First"
                                      disabled={isSuccess}
                                      {...register(`rows.${index}.first_name` as const, {
                                        onChange: () => handleFieldChange(index),
                                      })}
                                      data-e2e={`bulk-invite-first-name-${index}`}
                                      data-testid={`bulk-invite-first-name-${index}`}
                                    />
                                    {errors.rows?.[index]?.first_name && (
                                      <Text fontSize="2xs" color="text.danger" mt={0.5}>
                                        {errors.rows[index]?.first_name?.message}
                                      </Text>
                                    )}
                                  </Field.Root>
                                </Table.Cell>

                                <Table.Cell>
                                  <Field.Root invalid={!!errors.rows?.[index]?.last_name}>
                                    <InputField
                                      size="sm"
                                      aria-label={`Last name — row ${index + 1}`}
                                      placeholder="Last"
                                      disabled={isSuccess}
                                      {...register(`rows.${index}.last_name` as const, {
                                        onChange: () => handleFieldChange(index),
                                      })}
                                      data-e2e={`bulk-invite-last-name-${index}`}
                                      data-testid={`bulk-invite-last-name-${index}`}
                                    />
                                    {errors.rows?.[index]?.last_name && (
                                      <Text fontSize="2xs" color="text.danger" mt={0.5}>
                                        {errors.rows[index]?.last_name?.message}
                                      </Text>
                                    )}
                                  </Field.Root>
                                </Table.Cell>

                                <Table.Cell>
                                  <Field.Root invalid={!!errors.rows?.[index]?.role}>
                                    <NativeSelect.Root size="sm">
                                      <NativeSelect.Field
                                        aria-label={`Role — row ${index + 1}`}
                                        placeholder={t('users.selectRole', 'Select role')}
                                        disabled={isSuccess}
                                        {...register(`rows.${index}.role` as const, {
                                          onChange: () => handleFieldChange(index),
                                        })}
                                        data-e2e={`bulk-invite-role-${index}`}
                                        data-testid={`bulk-invite-role-${index}`}
                                      >
                                        {groupsData?.results
                                          ?.filter((g): g is Group => VALID_ROLES.has(g.name.toLowerCase()))
                                          .map((g) => (
                                            <option key={g.id} value={g.name}>
                                              {g.name}
                                            </option>
                                          ))}
                                      </NativeSelect.Field>
                                      <NativeSelect.Indicator />
                                    </NativeSelect.Root>
                                    {errors.rows?.[index]?.role && (
                                      <Text fontSize="2xs" color="text.danger" mt={0.5}>
                                        {errors.rows[index]?.role?.message}
                                      </Text>
                                    )}
                                  </Field.Root>
                                </Table.Cell>

                                <Table.Cell>
                                  <Field.Root invalid={!!errors.rows?.[index]?.site}>
                                    <NativeSelect.Root size="sm">
                                      <NativeSelect.Field
                                        aria-label={`Site — row ${index + 1}`}
                                        placeholder={t('users.selectSite', 'Select site')}
                                        disabled={isSuccess}
                                        {...register(`rows.${index}.site` as const, {
                                          onChange: () => handleFieldChange(index),
                                        })}
                                        data-e2e={`bulk-invite-site-${index}`}
                                        data-testid={`bulk-invite-site-${index}`}
                                      >
                                        {sitesData?.results?.map((s: Site) => (
                                          <option key={s.id} value={String(s.id)}>
                                            {s.name}
                                          </option>
                                        ))}
                                      </NativeSelect.Field>
                                      <NativeSelect.Indicator />
                                    </NativeSelect.Root>
                                    {errors.rows?.[index]?.site && (
                                      <Text fontSize="2xs" color="text.danger" mt={0.5}>
                                        {errors.rows[index]?.site?.message}
                                      </Text>
                                    )}
                                  </Field.Root>
                                </Table.Cell>

                                <Table.Cell>
                                  <HStack gap={1}>
                                    {isSuccess ? (
                                      <StatusBadge
                                        status={outcome.status === 'created' ? 'success' : 'default'}
                                        label={
                                          outcome.status === 'created'
                                            ? t('users.createdStatus', 'Created')
                                            : t('users.skippedStatus', 'Skipped')
                                        }
                                      />
                                    ) : (
                                      <IconButton
                                        aria-label={t('users.removeRow', 'Remove row')}
                                        variant="ghost"
                                        size="xs"
                                        color="text.danger"
                                        onClick={() => remove(index)}
                                        disabled={fields.length <= 1}
                                        data-e2e={`bulk-invite-remove-row-${index}`}
                                      >
                                        <HiTrash />
                                      </IconButton>
                                    )}
                                  </HStack>
                                </Table.Cell>
                              </Table.Row>
                              {hasError && (
                                <Table.Row bg="red.50/5">
                                  <Table.Cell colSpan={6} py={1} pl={4}>
                                    <Text fontSize="2xs" color="text.danger" fontWeight="medium">
                                      Error: {outcome.reason || t('users.validationFailed', 'Validation failed')}
                                    </Text>
                                  </Table.Cell>
                                </Table.Row>
                              )}
                            </Fragment>
                          );
                        })}
                      </Table.Body>
                    </Table.Root>
                  </Box>

                  <HStack justify="space-between" align="center">
                    <ButtonComponent
                      type="button"
                      visual="secondary"
                      size="sm"
                      icon={<HiPlus />}
                      onClick={() => append({ email: '', first_name: '', last_name: '', role: '', site: '' })}
                      data-e2e="bulk-invite-add-row"
                    >
                      {t('users.addRow', 'Add Row')}
                    </ButtonComponent>
                  </HStack>
                </>
              )}

              <HStack justify="flex-end" gap={3} borderTopWidth="1px" borderColor="border.secondary" pt={4}>
                <ButtonComponent
                  type="button"
                  visual="secondary"
                  onClick={handleClose}
                  disabled={isPending}
                  data-e2e="bulk-invite-cancel"
                >
                  {t('common.cancel', 'Cancel')}
                </ButtonComponent>
                <ButtonComponent
                  type="button"
                  visual="primary"
                  disabled={isPending || fields.length === 0}
                  onClick={methods.handleSubmit(onSubmitSeveral)}
                  data-e2e="bulk-invite-submit-several"
                >
                  {isPending ? t('users.submitting', 'Submitting...') : t('users.submitInvites', 'Submit Invites')}
                </ButtonComponent>
              </HStack>
            </VStack>
          </FormProvider>
        )}
      </VStack>
    </DialogComponent>
  );
}
